import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Settings, Users, UserCog, Wallet, ShieldCheck, CalendarDays, Palmtree,
    Award, HandCoins, BellRing, Receipt, Plane,
    Plus, Pencil, Trash2, Save, Loader2, ChevronDown, X, Search,
} from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchDCAForHRRules, fetchSubDCAByDCACode, fetchStatesList, fetchEmpCostCenters,
    fetchAllEmpGroups, submitEmpGroup, clearGroupSaveResult,
    fetchAllEmpCategories, submitNewEmpCategory, submitUpdateEmpCategory, clearCategorySaveResult,
    fetchGroupForSalaryRule, fetchAllSalaryHeads, fetchSalaryHeadType, submitNewSalaryHead,
    fetchAllStaffSalryHeads, fetchCostCenterStatesForMinWage, fetchAllMinWageRule,
    submitSalaryRule, submitDeleteSalaryRule, clearSalaryRuleSaveResult, clearNewSalaryHeadSaveResult,
    fetchPFESIRuleData, submitPFESIRule, clearPFESISaveResult,
    fetchAllHolidays, submitNewHoliday, submitDeleteHoliday, clearHolidaySaveResult,
    fetchEmpLeaveTypes, fetchLeaveRuleYears, fetchGroupsPaidLeaves,
    fetchCheckCustomLeaveExistForPageload, fetchCheckCustomLeaveExist, clearLeaveExistCheck,
    fetchAllLPaidLeavesData, submitELorPaidLeaveRule, submitDeleteELPaidLeave, clearELPaidLeaveSaveResult,
    fetchGratuityRuleData, submitGratuityRule, clearGratuitySaveResult,
    fetchGroupsForAdvance, fetchAllAdvanceRuleData, submitAdvanceRule, submitDeleteAdvanceRule, clearAdvanceSaveResult,
    fetchGroupsForNoticePeriod, fetchNotificationToRole, fetchNoticePeriodData,
    submitNoticePeriodRule, submitDeleteNoticePeriod, clearNoticePeriodSaveResult,
    fetchGroupsForTaxRule, fetchStatesListForTax, fetchProfessionalTaxData,
    submitTaxRule, submitDeleteTaxRule, clearTaxSaveResult,
    fetchGroupsForTravelRule, fetchAllHRTravelExpRules, submitTravelRule, submitDeleteTravelRule, clearTravelSaveResult,
    selectHRRules,
} from '../../slices/HRSlice/hrRulesSlice';

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI primitives
// ─────────────────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';
const selectCls = inputCls + ' appearance-none pr-10 cursor-pointer';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children, type = 'button', className = '' }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm', lg: 'px-6 py-2.5 text-sm' };
    const variants = {
        primary:   'bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-300 shadow-md hover:shadow-lg',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
        danger:    'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 text-rose-600 dark:text-rose-400 hover:bg-rose-100',
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green:  'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red:    'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-700',
        gray:   'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${colors[color]}`}>{children}</span>;
};

const SectionCard = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => (
    <div className="py-12 text-center text-gray-400 dark:text-gray-500 text-sm">
        <Search className="h-8 w-8 mx-auto mb-2 opacity-40" />
        {message}
    </div>
);

const MultiCheckList = ({ options, selected, onToggle, getId = (o) => o.State_ID, getLabel = (o) => o.State_Name, loading }) => (
    <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-40 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
        ) : options.length === 0 ? (
            <span className="text-xs text-gray-400 col-span-full">No options</span>
        ) : (
            options.map((o) => {
                const id = getId(o);
                const checked = selected.includes(id);
                return (
                    <label key={id} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={() => onToggle(id)} className="rounded border-gray-300" />
                        {getLabel(o)}
                    </label>
                );
            })
        )}
    </div>
);

const DCAPairSelector = ({ label = 'Account Head', dcaValue, subDcaValue, onDCAChange, onSubDCAChange, dcaList, dcaLoading, subDcaList, subDcaLoading }) => (
    <>
        <div>
            <Label required>{label}</Label>
            <SelectWrap loading={dcaLoading}>
                <select className={selectCls} value={dcaValue} onChange={(e) => onDCAChange(e.target.value)}>
                    <option value="">— Select —</option>
                    {dcaList.map((d) => <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                </select>
            </SelectWrap>
        </div>
        <div>
            <Label required>Sub {label}</Label>
            <SelectWrap loading={subDcaLoading}>
                <select className={selectCls} value={subDcaValue} onChange={(e) => onSubDCAChange(e.target.value)} disabled={!dcaValue}>
                    <option value="">— Select —</option>
                    {subDcaList.map((s) => <option key={s.SubDCAID} value={s.SubDCACode}>{s.SubDCAIDSTR}</option>)}
                </select>
            </SelectWrap>
        </div>
    </>
);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** CustomDatePicker's onChange hands back a Date object -> backend "dd-MMM-yyyy" string */
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const joinIds = (arr) => (arr && arr.length ? arr.join(',') + ',' : '');

const YES_NO = ['Yes', 'No'];
const APPLICABLE_TYPES = ['Yearly', 'Monthly', 'Daily'];
const MANDATORY_TYPES = ['Mandatory', 'Optional'];
const PCT_TYPES = ['Percentage', 'Rs', 'None'];
const HEAD_TYPES = ['Earning', 'Deduction', 'Benefit', 'OtherBenefit'];
const SKILL_TYPES = ['Skilled', 'Semi-Skilled', 'Unskilled', 'High-Skilled'];
const CREDIT_CYCLES = ['Monthly', 'Quarterly', 'Yearly'];

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
    { id: 'groups',      label: 'Groups',           icon: Users },
    { id: 'category',    label: 'Category',         icon: UserCog },
    { id: 'salarywages', label: 'Salary/Min Wages', icon: Wallet },
    { id: 'pfesi',       label: 'PF, ESI',          icon: ShieldCheck },
    { id: 'holidays',    label: 'Holidays',         icon: CalendarDays },
    { id: 'elpaidleave', label: 'EL/Paid Leave',    icon: Palmtree },
    { id: 'gratuity',    label: 'Gratuity',         icon: Award },
    { id: 'advance',     label: 'Advance',          icon: HandCoins },
    { id: 'noticeperiod',label: 'Notice Period',    icon: BellRing },
    { id: 'tax',         label: 'Tax',              icon: Receipt },
    { id: 'travel',      label: 'Official Travel',  icon: Plane },
];

// ═════════════════════════════════════════════════════════════════════════════
// TAB 1 — GROUPS
// ═════════════════════════════════════════════════════════════════════════════

const BLANK_GROUP = { GroupId: 0, GroupName: '', Attendance: 'No', Salary: 'No', DirectAppoint: 'No', DailyWageSalary: 'No' };

const GroupsTab = ({ currentUser }) => {
    const dispatch = useDispatch();
    const { groups, groupsLoading, groupSaveLoading, groupSaveResult, groupSaveError } = useSelector(selectHRRules);
    const [form, setForm] = useState(BLANK_GROUP);

    const load = useCallback(() => dispatch(fetchAllEmpGroups()), [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (groupSaveResult) {
            toast.success('Group saved successfully.');
            dispatch(clearGroupSaveResult());
            setForm(BLANK_GROUP);
            load();
        }
        if (groupSaveError) { toast.error(groupSaveError); dispatch(clearGroupSaveResult()); }
    }, [groupSaveResult, groupSaveError]); // eslint-disable-line

    const handleSubmit = () => {
        if (!form.GroupName.trim() || !/^[a-zA-Z0-9 ]+$/.test(form.GroupName.trim())) {
            toast.warning('Enter a valid alphanumeric group name.'); return;
        }
        dispatch(submitEmpGroup({ ...form, Action: 'New', CreatedBy: currentUser }));
    };

    const handleToggleStatus = (row) => {
        dispatch(submitEmpGroup({ ...row, Action: row.GroupStatus === 'Active' ? 'Delete' : 'Activate', CreatedBy: currentUser }));
    };

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Add Employee Group</span>
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
                    <div className="md:col-span-2">
                        <Label required>Group Name</Label>
                        <input className={inputCls} value={form.GroupName} onChange={(e) => setForm((p) => ({ ...p, GroupName: e.target.value }))} placeholder="e.g. Field Staff" />
                    </div>
                    {['Attendance', 'Salary', 'DirectAppoint', 'DailyWageSalary'].map((key) => (
                        <label key={key} className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                            <input type="checkbox" checked={form[key] === 'Yes'} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.checked ? 'Yes' : 'No' }))} className="rounded border-gray-300" />
                            {key === 'DirectAppoint' ? 'Direct Appoint' : key === 'DailyWageSalary' ? 'Daily Wage Salary' : key}
                        </label>
                    ))}
                    <Btn onClick={handleSubmit} loading={groupSaveLoading}><Plus className="h-3.5 w-3.5" />Add Group</Btn>
                </div>
            </SectionCard>

            <SectionCard>
                {groupsLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : groups.length === 0 ? (
                    <EmptyState message="No groups configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Group Name', 'Attendance', 'Salary', 'Direct Appoint', 'Daily Wage', 'Status', ''].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {groups.map((row) => (
                                    <tr key={row.GroupId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.GroupName}</td>
                                        <td className="px-3 py-2.5"><Badge color={row.Attendance === 'Yes' ? 'green' : 'gray'}>{row.Attendance}</Badge></td>
                                        <td className="px-3 py-2.5"><Badge color={row.Salary === 'Yes' ? 'green' : 'gray'}>{row.Salary}</Badge></td>
                                        <td className="px-3 py-2.5"><Badge color={row.DirectAppoint === 'Yes' ? 'green' : 'gray'}>{row.DirectAppoint}</Badge></td>
                                        <td className="px-3 py-2.5"><Badge color={row.DailyWageSalary === 'Yes' ? 'green' : 'gray'}>{row.DailyWageSalary}</Badge></td>
                                        <td className="px-3 py-2.5"><Badge color={row.GroupStatus === 'Active' ? 'green' : 'red'}>{row.GroupStatus}</Badge></td>
                                        <td className="px-3 py-2.5">
                                            <Btn size="sm" variant={row.GroupStatus === 'Active' ? 'danger' : 'secondary'} onClick={() => handleToggleStatus(row)} loading={groupSaveLoading}>
                                                {row.GroupStatus === 'Active' ? 'Deactivate' : 'Activate'}
                                            </Btn>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 2 — EMPLOYEE CATEGORY
// ═════════════════════════════════════════════════════════════════════════════

const BLANK_CATEGORY = { CategoryId: 0, CategoryName: '', ReportToId: 0, CategoryCode: '', RoleId: 0 };

const CategoryTab = ({ currentUser }) => {
    const dispatch = useDispatch();
    const { categories, categoriesLoading, categorySaveLoading, categorySaveResult, categorySaveError } = useSelector(selectHRRules);
    const [form, setForm] = useState(BLANK_CATEGORY);
    const [editing, setEditing] = useState(false);

    const load = useCallback(() => dispatch(fetchAllEmpCategories()), [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (categorySaveResult) {
            toast.success('Category saved successfully.');
            dispatch(clearCategorySaveResult());
            setForm(BLANK_CATEGORY);
            setEditing(false);
            load();
        }
        if (categorySaveError) { toast.error(categorySaveError); dispatch(clearCategorySaveResult()); }
    }, [categorySaveResult, categorySaveError]); // eslint-disable-line

    const handleSubmit = () => {
        if (!form.CategoryName.trim()) { toast.warning('Enter a category name.'); return; }
        if (!/^[A-Z]+$/.test(form.CategoryCode.trim())) { toast.warning('Category code must be capital letters only.'); return; }
        if (editing) {
            dispatch(submitUpdateEmpCategory({ EmpCategorysData: { ...form }, CreatedBy: currentUser }));
        } else {
            dispatch(submitNewEmpCategory({
                EmpCategorysData: { CategoryName: form.CategoryName, ReportToId: form.ReportToId, Action: 'New', CategoryCode: form.CategoryCode },
                CreatedBy: currentUser,
            }));
        }
    };

    const handleEdit = (row) => {
        setForm({ CategoryId: row.CategoryId, CategoryName: row.CategoryName, ReportToId: row.ReportToId, CategoryCode: row.CategoryCode, RoleId: row.RoleId || 0 });
        setEditing(true);
    };

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-100">{editing ? 'Edit Category' : 'Add Employee Category'}</span>
                    {editing && <button onClick={() => { setForm(BLANK_CATEGORY); setEditing(false); }} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>}
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <Label required>Category Name</Label>
                        <input className={inputCls} value={form.CategoryName} onChange={(e) => setForm((p) => ({ ...p, CategoryName: e.target.value }))} />
                    </div>
                    <div>
                        <Label required>Category Code</Label>
                        <input className={inputCls} value={form.CategoryCode} disabled={editing}
                            onChange={(e) => setForm((p) => ({ ...p, CategoryCode: e.target.value.toUpperCase() }))} placeholder="e.g. MGR" />
                    </div>
                    <div>
                        <Label>Report To</Label>
                        <SelectWrap loading={categoriesLoading}>
                            <select className={selectCls} value={form.ReportToId} onChange={(e) => setForm((p) => ({ ...p, ReportToId: parseInt(e.target.value, 10) || 0 }))}>
                                <option value={0}>— None —</option>
                                {categories.filter((c) => c.CategoryId !== form.CategoryId).map((c) => (
                                    <option key={c.CategoryId} value={c.CategoryId}>{c.CategoryName}</option>
                                ))}
                            </select>
                        </SelectWrap>
                    </div>
                    <Btn onClick={handleSubmit} loading={categorySaveLoading}>
                        {editing ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                        {editing ? 'Update' : 'Add Category'}
                    </Btn>
                </div>
            </SectionCard>

            <SectionCard>
                {categoriesLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : categories.length === 0 ? (
                    <EmptyState message="No employee categories configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Category Name', 'Report To', 'Category Code', ''].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {categories.map((row) => (
                                    <tr key={row.CategoryId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.CategoryName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.ReportTo || '—'}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.CategoryCode}</td>
                                        <td className="px-3 py-2.5">
                                            <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors">
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 3 — SALARY / MIN WAGES
// ═════════════════════════════════════════════════════════════════════════════

const BLANK_HEAD_ROW = () => ({
    HeadName: '', HeadType: '', PctType: '', Value: '', HeadDCA: '', HeadSubDCA: '',
    Applicable: '', Mandatory: '', ForPL: 'No', ForESI: 'No',
});

const SalaryMinWagesTab = ({ currentUser, dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const {
        salaryGroups, salaryGroupsLoading, salaryHeadsMaster, salaryHeadsMasterLoading,
        statesList, statesLoading, staffSalaryHeadRules, staffSalaryHeadRulesLoading,
        minWageStates, minWageStatesLoading, minWageRules, minWageRulesLoading,
        salaryRuleSaveLoading, salaryRuleSaveResult, salaryRuleSaveError,
        newSalaryHeadSaveLoading, newSalaryHeadSaveResult, newSalaryHeadSaveError,
    } = useSelector(selectHRRules);

    const [subTab, setSubTab] = useState('heads'); // 'heads' | 'minwage'

    // ── Salary Heads sub-form ──
    const [group, setGroup] = useState('');
    const [effDate, setEffDate] = useState('');
    const [selStates, setSelStates] = useState([]);
    const [basicAmt, setBasicAmt] = useState('');
    const [basicType, setBasicType] = useState('');
    const [basicDCA, setBasicDCA] = useState('');
    const [basicSubDCA, setBasicSubDCA] = useState('');
    const [daAmt, setDaAmt] = useState('');
    const [daType, setDaType] = useState('');
    const [daDCA, setDaDCA] = useState('');
    const [daSubDCA, setDaSubDCA] = useState('');
    const [rows, setRows] = useState([BLANK_HEAD_ROW()]);
    const [showNewHead, setShowNewHead] = useState(false);
    const [newHeadName, setNewHeadName] = useState('');
    const [newHeadType, setNewHeadType] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);

    // ── Min Wages sub-form ──
    const [mwSkill, setMwSkill] = useState('');
    const [mwStates, setMwStates] = useState([]);
    const [mwAmount, setMwAmount] = useState('');
    const [mwDCA, setMwDCA] = useState('');
    const [mwSubDCA, setMwSubDCA] = useState('');

    useEffect(() => {
        dispatch(fetchGroupForSalaryRule());
        dispatch(fetchAllSalaryHeads('None'));
        dispatch(fetchStatesList());
        dispatch(fetchAllStaffSalryHeads());
        dispatch(fetchAllMinWageRule());
    }, [dispatch]);

    useEffect(() => {
        if (salaryRuleSaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearSalaryRuleSaveResult());
            dispatch(fetchAllStaffSalryHeads());
            dispatch(fetchAllMinWageRule());
            resetHeadsForm();
            resetMinWageForm();
        }
        if (salaryRuleSaveError) { toast.error(salaryRuleSaveError); dispatch(clearSalaryRuleSaveResult()); }
    }, [salaryRuleSaveResult, salaryRuleSaveError]); // eslint-disable-line

    useEffect(() => {
        if (newSalaryHeadSaveResult) {
            toast.success('Salary head added.');
            dispatch(clearNewSalaryHeadSaveResult());
            dispatch(fetchAllSalaryHeads('None'));
            setShowNewHead(false); setNewHeadName(''); setNewHeadType('');
        }
        if (newSalaryHeadSaveError) { toast.error(newSalaryHeadSaveError); dispatch(clearNewSalaryHeadSaveResult()); }
    }, [newSalaryHeadSaveResult, newSalaryHeadSaveError]); // eslint-disable-line

    const resetHeadsForm = () => {
        setGroup(''); setEffDate(''); setSelStates([]);
        setBasicAmt(''); setBasicType(''); setBasicDCA(''); setBasicSubDCA('');
        setDaAmt(''); setDaType(''); setDaDCA(''); setDaSubDCA('');
        setRows([BLANK_HEAD_ROW()]);
    };
    const resetMinWageForm = () => { setMwSkill(''); setMwStates([]); setMwAmount(''); setMwDCA(''); setMwSubDCA(''); };

    const toggleState = (id) => setSelStates((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    const toggleMwState = (id) => setMwStates((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const updateRow = (idx, patch) => setRows((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const addRow = () => setRows((p) => [...p, BLANK_HEAD_ROW()]);
    const removeRow = (idx) => setRows((p) => p.filter((_, i) => i !== idx));

    const handleHeadNameChange = async (idx, headName) => {
        updateRow(idx, { HeadName: headName, HeadType: '' });
        if (headName) {
            const res = await dispatch(fetchSalaryHeadType(headName));
            if (res.payload) updateRow(idx, { HeadName: headName, HeadType: res.payload });
        }
    };

    const handleDCAChange = (code, kind) => {
        if (kind === 'basic') setBasicDCA(code);
        else if (kind === 'da') setDaDCA(code);
        else if (kind === 'mw') setMwDCA(code);
        if (code && !subDcaByDca[code]) onLoadSubDca(code);
    };
    const handleRowDCAChange = (idx, code) => {
        updateRow(idx, { HeadDCA: code, HeadSubDCA: '' });
        if (code && !subDcaByDca[code]) onLoadSubDca(code);
    };

    const handleSaveNewHead = () => {
        if (!newHeadName.trim() || !newHeadType) { toast.warning('Enter head name and type.'); return; }
        dispatch(submitNewSalaryHead({ HeadName: newHeadName.trim().toUpperCase(), HeadType: newHeadType, CreatedBy: currentUser }));
    };

    const handleSubmitHeads = () => {
        if (!group || !effDate || selStates.length === 0) { toast.warning('Group, Effective Date and at least one State are required.'); return; }
        if (!basicAmt || !basicType || !basicDCA || !basicSubDCA) { toast.warning('Complete the Basic amount details.'); return; }
        const headNames = rows.filter((r) => r.HeadName).map((r) => r.HeadName);
        if (headNames.some((h) => h.toUpperCase() === 'DA')) { toast.warning('"DA" cannot be added as a row — use the DA field above.'); return; }
        if (new Set(headNames).size !== headNames.length) { toast.warning('Duplicate head names are not allowed.'); return; }
        for (const r of rows) {
            if (!r.HeadName) continue;
            if (!r.HeadType || !r.PctType || (r.PctType !== 'None' && !r.Value) || !r.HeadDCA || !r.HeadSubDCA || !r.Applicable || !r.Mandatory) {
                toast.warning(`Complete all fields for head "${r.HeadName}".`); return;
            }
        }
        const used = rows.filter((r) => r.HeadName);
        dispatch(submitSalaryRule({
            GroupId: group,
            State: joinIds(selStates),
            EffectiveDate: toDDMonYYYY(effDate),
            HeadNames: joinIds(used.map((r) => r.HeadName)),
            HeadTypes: joinIds(used.map((r) => r.HeadType)),
            PercentageorAmounts: joinIds(used.map((r) => r.Value)),
            PercentageorAmountstypes: joinIds(used.map((r) => r.PctType)),
            SalaryBasic: parseFloat(basicAmt) || 0,
            StaffType: 'Staff',
            DCA: basicDCA, SubDCA: basicSubDCA,
            HeadDCA: joinIds(used.map((r) => r.HeadDCA)),
            HeadSubDCA: joinIds(used.map((r) => r.HeadSubDCA)),
            DA: parseFloat(daAmt) || 0,
            DADca: daDCA, DASubDca: daSubDCA,
            ApplicableType: joinIds(used.map((r) => r.Applicable)),
            IsMandatory: joinIds(used.map((r) => r.Mandatory)),
            BasicAmountApplicableType: basicType,
            DAAmountApplicableType: daType,
            ApplicableForPL: joinIds(used.map((r) => r.ForPL)),
            ApplicableForESI: joinIds(used.map((r) => r.ForESI)),
            CreatedBy: currentUser,
        }));
    };

    const handleDeleteHeadsRow = (row) => {
        dispatch(submitDeleteSalaryRule({ GroupId: row.GroupId, StateId: row.StateId, StaffType: 'Staff', CreatedBy: currentUser }));
    };

    const handleSubmitMinWage = () => {
        if (!mwSkill || mwStates.length === 0 || !mwAmount || !mwDCA || !mwSubDCA) { toast.warning('All fields are required.'); return; }
        dispatch(submitSalaryRule({
            State: joinIds(mwStates), EffectiveDate: toDDMonYYYY(new Date()), SkillType: mwSkill,
            SalaryBasic: parseFloat(mwAmount) || 0, StaffType: 'Labour', DCA: mwDCA, SubDCA: mwSubDCA, CreatedBy: currentUser,
        }));
    };

    const handleDeleteMinWageRow = (row) => {
        dispatch(submitDeleteSalaryRule({ MinimumWageData: { Stateid: row.Stateid, LabourWageId: row.LabourWageId, SkillType: row.SkillType }, StaffType: 'Labour', CreatedBy: currentUser }));
    };

    return (
        <div className="space-y-5">
            <div className="flex gap-2">
                <button onClick={() => setSubTab('heads')} className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${subTab === 'heads' ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>Salary Heads</button>
                <button onClick={() => setSubTab('minwage')} className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${subTab === 'minwage' ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>Minimum Wages</button>
            </div>

            {subTab === 'heads' ? (
                <>
                    <SectionCard>
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Salary Heads Rule</span>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <Label required>Group</Label>
                                    <SelectWrap loading={salaryGroupsLoading}>
                                        <select className={selectCls} value={group} onChange={(e) => setGroup(e.target.value)}>
                                            <option value="">— Select —</option>
                                            {salaryGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                                        </select>
                                    </SelectWrap>
                                </div>
                                <div>
                                    <Label required>Effective Date</Label>
                                    <CustomDatePicker value={effDate} onChange={setEffDate} placeholder="Select date" />
                                </div>
                                <div className="md:col-span-2">
                                    <Label required>States</Label>
                                    <MultiCheckList options={statesList} selected={selStates} onToggle={toggleState} loading={statesLoading} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-900/30">
                                <div>
                                    <Label required>Basic Amount</Label>
                                    <input className={inputCls} value={basicAmt} onChange={(e) => setBasicAmt(e.target.value)} inputMode="decimal" />
                                </div>
                                <div>
                                    <Label required>Basic Applicable</Label>
                                    <SelectWrap>
                                        <select className={selectCls} value={basicType} onChange={(e) => setBasicType(e.target.value)}>
                                            <option value="">— Select —</option>
                                            {APPLICABLE_TYPES.map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </SelectWrap>
                                </div>
                                <DCAPairSelector label="Basic DCA" dcaValue={basicDCA} subDcaValue={basicSubDCA}
                                    onDCAChange={(c) => handleDCAChange(c, 'basic')} onSubDCAChange={setBasicSubDCA}
                                    dcaList={dcaList} dcaLoading={dcaLoading}
                                    subDcaList={subDcaByDca[basicDCA] || []} subDcaLoading={subDcaLoadingByDca[basicDCA]} />
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-900/30">
                                <div>
                                    <Label>DA Amount</Label>
                                    <input className={inputCls} value={daAmt} onChange={(e) => setDaAmt(e.target.value)} inputMode="decimal" />
                                </div>
                                <div>
                                    <Label>DA Applicable</Label>
                                    <SelectWrap>
                                        <select className={selectCls} value={daType} onChange={(e) => setDaType(e.target.value)}>
                                            <option value="">— Select —</option>
                                            {APPLICABLE_TYPES.map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </SelectWrap>
                                </div>
                                <DCAPairSelector label="DA DCA" dcaValue={daDCA} subDcaValue={daSubDCA}
                                    onDCAChange={(c) => handleDCAChange(c, 'da')} onSubDCAChange={setDaSubDCA}
                                    dcaList={dcaList} dcaLoading={dcaLoading}
                                    subDcaList={subDcaByDca[daDCA] || []} subDcaLoading={subDcaLoadingByDca[daDCA]} />
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Salary Head Rows</span>
                                    <div className="flex gap-2">
                                        <Btn size="sm" variant="secondary" onClick={() => setShowNewHead(true)}><Plus className="h-3 w-3" />New Salary Head</Btn>
                                        <Btn size="sm" variant="secondary" onClick={addRow}><Plus className="h-3 w-3" />Add Row</Btn>
                                    </div>
                                </div>
                                {rows.map((row, idx) => (
                                    <div key={idx} className="grid grid-cols-2 md:grid-cols-9 gap-2 items-end p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                                        <div className="md:col-span-1">
                                            <Label>Head</Label>
                                            <SelectWrap loading={salaryHeadsMasterLoading}>
                                                <select className={selectCls} value={row.HeadName} onChange={(e) => handleHeadNameChange(idx, e.target.value)}>
                                                    <option value="">—</option>
                                                    {salaryHeadsMaster.map((h) => <option key={h.HeadName} value={h.HeadName}>{h.HeadName}</option>)}
                                                </select>
                                            </SelectWrap>
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>Type</Label>
                                            <input className={inputCls} value={row.HeadType} disabled />
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>Pct/Amt</Label>
                                            <SelectWrap>
                                                <select className={selectCls} value={row.PctType} onChange={(e) => updateRow(idx, { PctType: e.target.value })}>
                                                    <option value="">—</option>
                                                    {PCT_TYPES.map((t) => <option key={t}>{t}</option>)}
                                                </select>
                                            </SelectWrap>
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>Value</Label>
                                            <input className={inputCls} value={row.Value} disabled={row.PctType === 'None'} onChange={(e) => updateRow(idx, { Value: e.target.value })} />
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>DCA</Label>
                                            <SelectWrap loading={dcaLoading}>
                                                <select className={selectCls} value={row.HeadDCA} onChange={(e) => handleRowDCAChange(idx, e.target.value)}>
                                                    <option value="">—</option>
                                                    {dcaList.map((d) => <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                                                </select>
                                            </SelectWrap>
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>Sub DCA</Label>
                                            <SelectWrap loading={subDcaLoadingByDca[row.HeadDCA]}>
                                                <select className={selectCls} value={row.HeadSubDCA} disabled={!row.HeadDCA} onChange={(e) => updateRow(idx, { HeadSubDCA: e.target.value })}>
                                                    <option value="">—</option>
                                                    {(subDcaByDca[row.HeadDCA] || []).map((s) => <option key={s.SubDCAID} value={s.SubDCACode}>{s.SubDCAIDSTR}</option>)}
                                                </select>
                                            </SelectWrap>
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>Applicable</Label>
                                            <SelectWrap>
                                                <select className={selectCls} value={row.Applicable} onChange={(e) => updateRow(idx, { Applicable: e.target.value })}>
                                                    <option value="">—</option>
                                                    {APPLICABLE_TYPES.map((t) => <option key={t}>{t}</option>)}
                                                </select>
                                            </SelectWrap>
                                        </div>
                                        <div className="md:col-span-1">
                                            <Label>Mandatory</Label>
                                            <SelectWrap>
                                                <select className={selectCls} value={row.Mandatory} onChange={(e) => updateRow(idx, { Mandatory: e.target.value })}>
                                                    <option value="">—</option>
                                                    {MANDATORY_TYPES.map((t) => <option key={t}>{t}</option>)}
                                                </select>
                                            </SelectWrap>
                                        </div>
                                        <div className="md:col-span-1 flex items-center gap-2">
                                            <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-500">
                                                <input type="checkbox" checked={row.ForPL === 'Yes'} onChange={(e) => updateRow(idx, { ForPL: e.target.checked ? 'Yes' : 'No' })} />PL
                                            </label>
                                            <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-500">
                                                <input type="checkbox" checked={row.ForESI === 'Yes'} onChange={(e) => updateRow(idx, { ForESI: e.target.checked ? 'Yes' : 'No' })} />ESI
                                            </label>
                                            {rows.length > 1 && (
                                                <button onClick={() => removeRow(idx)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-end">
                                <Btn onClick={handleSubmitHeads} loading={salaryRuleSaveLoading}><Save className="h-3.5 w-3.5" />Save Salary Rule</Btn>
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard>
                        {staffSalaryHeadRulesLoading ? (
                            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                        ) : staffSalaryHeadRules.length === 0 ? (
                            <EmptyState message="No salary head rules configured yet." />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                            {['Effective Date', 'Group', 'State', '', ''].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                        {staffSalaryHeadRules.map((row, i) => (
                                            <React.Fragment key={i}>
                                                <tr className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EffectiveDate}</td>
                                                    <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.GroupName}</td>
                                                    <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.State}</td>
                                                    <td className="px-3 py-2.5">
                                                        <button onClick={() => setExpandedRow(expandedRow === i ? null : i)} className="text-xs font-semibold text-indigo-600 hover:underline">
                                                            {expandedRow === i ? 'Hide heads' : 'View heads'}
                                                        </button>
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <button onClick={() => handleDeleteHeadsRow(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                                                    </td>
                                                </tr>
                                                {expandedRow === i && (
                                                    <tr>
                                                        <td colSpan={5} className="px-3 pb-3">
                                                            <table className="w-full text-xs border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                                                                <thead>
                                                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                                                        {['Head', 'Type', 'Pct/Amt', 'Value', 'DCA', 'SubDCA', 'Applicable', 'Mandatory', 'PL', 'ESI'].map((h) => (
                                                                            <th key={h} className="px-2 py-1.5 font-bold text-gray-500 uppercase">{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                                                    {(row.StaffSalaryHeadsList || []).map((h, hi) => (
                                                                        <tr key={hi}>
                                                                            <td className="px-2 py-1.5">{h.HeadName}</td>
                                                                            <td className="px-2 py-1.5">{h.HeadType}</td>
                                                                            <td className="px-2 py-1.5">{h.Type}</td>
                                                                            <td className="px-2 py-1.5">{h.Amount ?? h.StaffAmount}</td>
                                                                            <td className="px-2 py-1.5">{(h.HeadDCA || '').split(',')[1] || h.HeadDCA}</td>
                                                                            <td className="px-2 py-1.5">{(h.HeadSubDCA || '').split(',')[1] || h.HeadSubDCA}</td>
                                                                            <td className="px-2 py-1.5">{h.ApplicableType}</td>
                                                                            <td className="px-2 py-1.5">{h.MandatoryType}</td>
                                                                            <td className="px-2 py-1.5">{h.ApplicableForPL}</td>
                                                                            <td className="px-2 py-1.5">{h.ApplicableForESI}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </SectionCard>
                </>
            ) : (
                <>
                    <SectionCard>
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Minimum Wages Rule</span>
                        </div>
                        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                            <div>
                                <Label required>Skill Type</Label>
                                <SelectWrap>
                                    <select className={selectCls} value={mwSkill} onChange={(e) => { setMwSkill(e.target.value); setMwStates([]); dispatch(fetchCostCenterStatesForMinWage(e.target.value)); }}>
                                        <option value="">— Select —</option>
                                        {SKILL_TYPES.map((t) => <option key={t}>{t}</option>)}
                                    </select>
                                </SelectWrap>
                            </div>
                            <div className="md:col-span-2">
                                <Label required>States</Label>
                                <MultiCheckList options={minWageStates} selected={mwStates} onToggle={toggleMwState} loading={minWageStatesLoading} />
                            </div>
                            <div>
                                <Label required>Amount</Label>
                                <input className={inputCls} value={mwAmount} onChange={(e) => setMwAmount(e.target.value)} inputMode="decimal" />
                            </div>
                            <DCAPairSelector dcaValue={mwDCA} subDcaValue={mwSubDCA}
                                onDCAChange={(c) => handleDCAChange(c, 'mw')} onSubDCAChange={setMwSubDCA}
                                dcaList={dcaList} dcaLoading={dcaLoading}
                                subDcaList={subDcaByDca[mwDCA] || []} subDcaLoading={subDcaLoadingByDca[mwDCA]} />
                            <Btn onClick={handleSubmitMinWage} loading={salaryRuleSaveLoading}><Save className="h-3.5 w-3.5" />Save</Btn>
                        </div>
                    </SectionCard>

                    <SectionCard>
                        {minWageRulesLoading ? (
                            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                        ) : minWageRules.length === 0 ? (
                            <EmptyState message="No minimum wage rules configured yet." />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                            {['Effective Date', 'Skill Type', 'State', 'Amount', 'DCA', 'SubDCA', ''].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                        {minWageRules.map((row, i) => (
                                            <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EffectiveDate}</td>
                                                <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.SkillType}</td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.StateName}</td>
                                                <td className="px-3 py-2.5 font-semibold text-indigo-600 dark:text-indigo-400">{row.Amount}</td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{(row.DCA || '').split(',')[1] || row.DCA}</td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{(row.SubDCA || '').split(',')[1] || row.SubDCA}</td>
                                                <td className="px-3 py-2.5">
                                                    <button onClick={() => handleDeleteMinWageRow(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </SectionCard>
                </>
            )}

            {showNewHead && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Add New Salary Head</h3>
                            <button onClick={() => setShowNewHead(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <Label required>Head Name</Label>
                                <input className={inputCls} value={newHeadName} onChange={(e) => setNewHeadName(e.target.value.toUpperCase())} />
                            </div>
                            <div>
                                <Label required>Head Type</Label>
                                <SelectWrap>
                                    <select className={selectCls} value={newHeadType} onChange={(e) => setNewHeadType(e.target.value)}>
                                        <option value="">— Select —</option>
                                        {HEAD_TYPES.map((t) => <option key={t}>{t}</option>)}
                                    </select>
                                </SelectWrap>
                            </div>
                            <Btn onClick={handleSaveNewHead} loading={newSalaryHeadSaveLoading} className="w-full justify-center">Save</Btn>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 4 — PF & ESI
// ═════════════════════════════════════════════════════════════════════════════

const BLANK_PFESI = {
    EmployeeContribution: '', EmployerContribution: '', PFAdminCharge: '', PFEffectDate: '',
    ESISalaryLimit: '', ESIPercentage: '', ESIEmployerPercentage: '', ESIEffectDate: '',
    Maxpfemployeeramount: '',
    PFDCA: '', PFSubDCA: '', PFEmployeeDCA: '', PFEmployeeSubDCA: '', ESIDCA: '', ESISubDCA: '',
    LBPFDCA: '', LBPFSubDCA: '', LBEmplrPFDCA: '', LBEmplrPFSubDCA: '', LBEsiDCA: '', LBEsiSubDCA: '',
};

const PFESITab = ({ currentUser, dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const { pfesiData, pfesiLoading, pfesiSaveLoading, pfesiSaveResult, pfesiSaveError } = useSelector(selectHRRules);
    const [form, setForm] = useState(BLANK_PFESI);

    useEffect(() => { dispatch(fetchPFESIRuleData()); }, [dispatch]);

    useEffect(() => {
        if (pfesiData) {
            setForm({
                EmployeeContribution: pfesiData.EmployeeContribution ?? '', EmployerContribution: pfesiData.EmployerContribution ?? '',
                PFAdminCharge: pfesiData.PFAdminCharge ?? '', PFEffectDate: pfesiData.PFEffectDate ?? '',
                ESISalaryLimit: pfesiData.ESISalaryLimit ?? '', ESIPercentage: pfesiData.ESIPercentage ?? '',
                ESIEmployerPercentage: pfesiData.ESIEmployerPercentage ?? '', ESIEffectDate: pfesiData.ESIEffectDate ?? '',
                Maxpfemployeeramount: pfesiData.Maxpfemployeeramount ?? '',
                PFDCA: pfesiData.PFDCA ?? '', PFSubDCA: pfesiData.PFSubDCA ?? '',
                PFEmployeeDCA: pfesiData.PFEmployeeDCA ?? '', PFEmployeeSubDCA: pfesiData.PFEmployeeSubDCA ?? '',
                ESIDCA: pfesiData.ESIDCA ?? '', ESISubDCA: pfesiData.ESISubDCA ?? '',
                LBPFDCA: pfesiData.LBPFDCA ?? '', LBPFSubDCA: pfesiData.LBPFSubDCA ?? '',
                LBEmplrPFDCA: pfesiData.LBEmplrPFDCA ?? '', LBEmplrPFSubDCA: pfesiData.LBEmplrPFSubDCA ?? '',
                LBEsiDCA: pfesiData.LBEsiDCA ?? '', LBEsiSubDCA: pfesiData.LBEsiSubDCA ?? '',
            });
        }
    }, [pfesiData]);

    useEffect(() => {
        if (pfesiSaveResult) { toast.success('PF/ESI rule saved.'); dispatch(clearPFESISaveResult()); dispatch(fetchPFESIRuleData()); }
        if (pfesiSaveError) { toast.error(pfesiSaveError); dispatch(clearPFESISaveResult()); }
    }, [pfesiSaveResult, pfesiSaveError]); // eslint-disable-line

    const set = (key) => (val) => {
        setForm((p) => ({ ...p, [key]: val }));
        if (key.endsWith('DCA') && !key.includes('Sub') && val && !subDcaByDca[val]) onLoadSubDca(val);
    };

    const dcaPairs = [
        ['PF Employer', 'PFDCA', 'PFSubDCA'],
        ['PF Employee', 'PFEmployeeDCA', 'PFEmployeeSubDCA'],
        ['ESI', 'ESIDCA', 'ESISubDCA'],
        ['Labour PF', 'LBPFDCA', 'LBPFSubDCA'],
        ['Labour PF Employer', 'LBEmplrPFDCA', 'LBEmplrPFSubDCA'],
        ['Labour ESI', 'LBEsiDCA', 'LBEsiSubDCA'],
    ];

    const handleSubmit = () => {
        const required = ['EmployeeContribution', 'EmployerContribution', 'PFAdminCharge', 'PFEffectDate', 'ESISalaryLimit', 'ESIPercentage', 'ESIEmployerPercentage', 'ESIEffectDate', 'Maxpfemployeeramount'];
        if (required.some((k) => form[k] === '')) { toast.warning('All amount/date fields are required.'); return; }
        if (dcaPairs.some(([, dcaKey, subKey]) => !form[dcaKey] || !form[subKey])) { toast.warning('All account head selections are required.'); return; }
        dispatch(submitPFESIRule({ ...form, CreatedBy: currentUser }));
    };

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-100">PF Configuration</span>
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><Label required>Employee Contribution %</Label><input className={inputCls} value={form.EmployeeContribution} onChange={(e) => set('EmployeeContribution')(e.target.value)} /></div>
                    <div><Label required>Employer Contribution %</Label><input className={inputCls} value={form.EmployerContribution} onChange={(e) => set('EmployerContribution')(e.target.value)} /></div>
                    <div><Label required>PF Admin Charge %</Label><input className={inputCls} value={form.PFAdminCharge} onChange={(e) => set('PFAdminCharge')(e.target.value)} /></div>
                    <div><Label required>Max PF Employer Amount</Label><input className={inputCls} value={form.Maxpfemployeeramount} onChange={(e) => set('Maxpfemployeeramount')(e.target.value)} /></div>
                    <div><Label required>PF Effective Date</Label><CustomDatePicker value={form.PFEffectDate} onChange={(v) => set('PFEffectDate')(toDDMonYYYY(v))} /></div>
                </div>
            </SectionCard>

            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-100">ESI Configuration</span>
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><Label required>ESI Salary Limit</Label><input className={inputCls} value={form.ESISalaryLimit} onChange={(e) => set('ESISalaryLimit')(e.target.value)} /></div>
                    <div><Label required>ESI Employee %</Label><input className={inputCls} value={form.ESIPercentage} onChange={(e) => set('ESIPercentage')(e.target.value)} /></div>
                    <div><Label required>ESI Employer %</Label><input className={inputCls} value={form.ESIEmployerPercentage} onChange={(e) => set('ESIEmployerPercentage')(e.target.value)} /></div>
                    <div><Label required>ESI Effective Date</Label><CustomDatePicker value={form.ESIEffectDate} onChange={(v) => set('ESIEffectDate')(toDDMonYYYY(v))} /></div>
                </div>
            </SectionCard>

            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Account Heads</span>
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {dcaPairs.map(([label, dcaKey, subKey]) => (
                        <DCAPairSelector key={dcaKey} label={label} dcaValue={form[dcaKey]} subDcaValue={form[subKey]}
                            onDCAChange={(c) => { set(dcaKey)(c); setForm((p) => ({ ...p, [subKey]: '' })); }}
                            onSubDCAChange={(v) => set(subKey)(v)}
                            dcaList={dcaList} dcaLoading={dcaLoading}
                            subDcaList={subDcaByDca[form[dcaKey]] || []} subDcaLoading={subDcaLoadingByDca[form[dcaKey]]} />
                    ))}
                </div>
                <div className="px-5 pb-5 flex justify-end">
                    <Btn onClick={handleSubmit} loading={pfesiSaveLoading || pfesiLoading}><Save className="h-3.5 w-3.5" />{pfesiData?.RuleId ? 'Update' : 'Submit'}</Btn>
                </div>
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 5 — HOLIDAYS
// ═════════════════════════════════════════════════════════════════════════════

const HOLIDAY_TYPES = ['Optional', 'Normal'];

const HolidaysTab = ({ currentUser }) => {
    const dispatch = useDispatch();
    const { holidays, holidaysLoading, statesList, statesLoading, holidaySaveLoading, holidaySaveResult, holidaySaveError } = useSelector(selectHRRules);
    const [holidayType, setHolidayType] = useState('');
    const [holidayName, setHolidayName] = useState('');
    const [holidayDate, setHolidayDate] = useState('');
    const [allStates, setAllStates] = useState(true);
    const [selStates, setSelStates] = useState([]);

    const load = useCallback(() => { dispatch(fetchAllHolidays()); dispatch(fetchStatesList()); }, [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (holidaySaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearHolidaySaveResult());
            setHolidayType(''); setHolidayName(''); setHolidayDate(''); setAllStates(true); setSelStates([]);
            load();
        }
        if (holidaySaveError) { toast.error(holidaySaveError); dispatch(clearHolidaySaveResult()); }
    }, [holidaySaveResult, holidaySaveError]); // eslint-disable-line

    const toggleState = (id) => setSelStates((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const handleSubmit = () => {
        if (!holidayType || !holidayName.trim() || !holidayDate) { toast.warning('All fields are required.'); return; }
        if (!allStates && selStates.length === 0) { toast.warning('Select at least one state, or mark as All States.'); return; }
        dispatch(submitNewHoliday({
            HolidaysData: { HolidayType: holidayType, HolidayDate: toDDMonYYYY(holidayDate), HolidayName: holidayName.trim(), HolidayState: allStates ? '0' : joinIds(selStates), Action: 'New' },
            CreatedBy: currentUser,
        }));
    };

    const handleDelete = (row) => {
        dispatch(submitDeleteHoliday({ HolidaysData: { HolidayName: row.HolidayName, HolidayDate: row.HolidayDate, Action: 'Delete' }, CreatedBy: currentUser }));
    };

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">Add Holiday</span></div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <Label required>Holiday Type</Label>
                            <SelectWrap>
                                <select className={selectCls} value={holidayType} onChange={(e) => setHolidayType(e.target.value)}>
                                    <option value="">— Select —</option>
                                    {HOLIDAY_TYPES.map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                        <div><Label required>Holiday Name</Label><input className={inputCls} value={holidayName} onChange={(e) => setHolidayName(e.target.value)} /></div>
                        <div><Label required>Holiday Date</Label><CustomDatePicker value={holidayDate} onChange={setHolidayDate} /></div>
                        <Btn onClick={handleSubmit} loading={holidaySaveLoading}><Plus className="h-3.5 w-3.5" />Add Holiday</Btn>
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
                            <input type="checkbox" checked={allStates} onChange={(e) => setAllStates(e.target.checked)} />Applicable to All States
                        </label>
                        {!allStates && <MultiCheckList options={statesList} selected={selStates} onToggle={toggleState} loading={statesLoading} />}
                    </div>
                </div>
            </SectionCard>

            <SectionCard>
                {holidaysLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : holidays.length === 0 ? (
                    <EmptyState message="No holidays configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Type', 'Name', 'Date', 'States', ''].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {holidays.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5"><Badge color="indigo">{row.HolidayType}</Badge></td>
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.HolidayName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.HolidayDate}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">
                                            {(row.SelectedstateDetails || []).length === 0 ? 'All' : row.SelectedstateDetails.map((s) => s.State_Name).join(', ')}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {row.pastDate !== '1' && (
                                                <button onClick={() => handleDelete(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 6 — EL / PAID LEAVE
// ═════════════════════════════════════════════════════════════════════════════

const ELPaidLeaveTab = ({ currentUser }) => {
    const dispatch = useDispatch();
    const {
        existingElPaidLeave, leaveTypes, leaveYears, paidLeaveGroups, paidLeaveGroupsLoading,
        elPaidLeaves, elPaidLeavesLoading, elPaidLeaveSaveLoading, elPaidLeaveSaveResult, elPaidLeaveSaveError,
    } = useSelector(selectHRRules);

    const [mode, setMode] = useState('Other'); // 'Other' | 'Custom'
    const [leaveTypeId, setLeaveTypeId] = useState('');
    const [year, setYear] = useState('');
    const [groupId, setGroupId] = useState('');
    const [noofLeaves, setNoofLeaves] = useState('');
    const [creditedby, setCreditedby] = useState('');
    const [carryFwd, setCarryFwd] = useState(false);
    const [customDate, setCustomDate] = useState('');

    const load = useCallback(() => {
        dispatch(fetchCheckCustomLeaveExistForPageload());
        dispatch(fetchEmpLeaveTypes());
        dispatch(fetchLeaveRuleYears());
        dispatch(fetchAllLPaidLeavesData());
    }, [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (leaveTypeId && year) dispatch(fetchGroupsPaidLeaves({ Leavetypeid: leaveTypeId, year }));
    }, [dispatch, leaveTypeId, year]);

    useEffect(() => {
        if (existingElPaidLeave?.leavetypeid === 7) setMode('Custom');
    }, [existingElPaidLeave]);

    useEffect(() => {
        if (elPaidLeaveSaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearELPaidLeaveSaveResult());
            setLeaveTypeId(''); setYear(''); setGroupId(''); setNoofLeaves(''); setCreditedby(''); setCarryFwd(false); setCustomDate('');
            load();
        }
        if (elPaidLeaveSaveError) { toast.error(elPaidLeaveSaveError); dispatch(clearELPaidLeaveSaveResult()); }
    }, [elPaidLeaveSaveResult, elPaidLeaveSaveError]); // eslint-disable-line

    const creditedPortion = () => {
        const n = parseFloat(noofLeaves) || 0;
        if (creditedby === 'Monthly') return n / 12;
        if (creditedby === 'Quarterly') return n / 4;
        if (creditedby === 'Yearly') return n;
        return 0;
    };

    const runExistCheck = async () => {
        const res = await dispatch(fetchCheckCustomLeaveExist({ leavetypeid: mode === 'Custom' ? 7 : leaveTypeId, GroupId: groupId || 0, Year: year || 0 }));
        return res.payload || '';
    };

    const handleSubmit = async () => {
        if (mode === 'Other') {
            if (!leaveTypeId || !year || !groupId || !noofLeaves || !creditedby) { toast.warning('All fields are required.'); return; }
        } else {
            if (!customDate) { toast.warning('Select an effective date.'); return; }
        }
        const msg = await runExistCheck();
        if (msg && msg.toLowerCase().includes('remove other')) {
            if (!window.confirm(msg)) return;
        } else if (msg) {
            toast.error(msg);
            dispatch(clearLeaveExistCheck());
            return;
        }
        dispatch(clearLeaveExistCheck());

        if (mode === 'Other') {
            dispatch(submitELorPaidLeaveRule({
                ELorPaidLeaveRuleData: {
                    LeaveTypeId: parseInt(leaveTypeId, 10), EffectiveYear: parseInt(year, 10), GroupId: parseInt(groupId, 10),
                    CarryforwardToNextYear: carryFwd ? 'Yes' : 'No', Action: 'New', CurrentLeaveTypeId: existingElPaidLeave?.leavetypeid || 0,
                    NoofLeaves: parseFloat(noofLeaves), LeavesPortion: 0, Creditedby: creditedby, NofleavesForCredited: creditedPortion(), LeavesPortion1: 0,
                },
                CreatedBy: currentUser,
            }));
        } else {
            dispatch(submitELorPaidLeaveRule({
                ELorPaidLeaveRuleData: { LeaveTypeId: 7, EffectiveDate: toDDMonYYYY(customDate), CarryforwardToNextYear: carryFwd ? 'Yes' : 'No', Action: 'New', CurrentLeaveTypeId: existingElPaidLeave?.leavetypeid || 0 },
                CreatedBy: currentUser,
            }));
        }
    };

    const handleDeleteCustom = () => {
        dispatch(submitDeleteELPaidLeave({ ELorPaidLeaveRuleData: { ELPaidId: existingElPaidLeave?.ELPaidId, LeaveTypeId: 7 }, CreatedBy: currentUser }));
    };

    const otherRows = elPaidLeaves.filter((r) => r.LeaveTypeId !== 7);

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-100">EL / Paid Leave Rule</span>
                    <div className="flex gap-2 ml-auto">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300">
                            <input type="radio" checked={mode === 'Other'} onChange={() => setMode('Other')} disabled={existingElPaidLeave?.leavetypeid === 7} />Other
                        </label>
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300">
                            <input type="radio" checked={mode === 'Custom'} onChange={() => setMode('Custom')} />Custom
                        </label>
                    </div>
                </div>
                <div className="p-5">
                    {mode === 'Other' ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                            <div>
                                <Label required>Leave Type</Label>
                                <SelectWrap>
                                    <select className={selectCls} value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)}>
                                        <option value="">— Select —</option>
                                        {leaveTypes.map((l) => <option key={l.LeaveTypeid} value={l.LeaveTypeid}>{l.LeaveTypeName}</option>)}
                                    </select>
                                </SelectWrap>
                            </div>
                            <div>
                                <Label required>Effective Year</Label>
                                <SelectWrap>
                                    <select className={selectCls} value={year} onChange={(e) => setYear(e.target.value)}>
                                        <option value="">— Select —</option>
                                        {leaveYears.map((y, i) => <option key={i} value={y.Year || y}>{y.Year || y}</option>)}
                                    </select>
                                </SelectWrap>
                            </div>
                            <div>
                                <Label required>Group</Label>
                                <SelectWrap loading={paidLeaveGroupsLoading}>
                                    <select className={selectCls} value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={!leaveTypeId || !year}>
                                        <option value="">— Select —</option>
                                        {paidLeaveGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                                    </select>
                                </SelectWrap>
                            </div>
                            <div><Label required>No. of Leaves</Label><input className={inputCls} value={noofLeaves} onChange={(e) => setNoofLeaves(e.target.value)} inputMode="decimal" /></div>
                            <div>
                                <Label required>Credited By</Label>
                                <SelectWrap>
                                    <select className={selectCls} value={creditedby} onChange={(e) => setCreditedby(e.target.value)}>
                                        <option value="">— Select —</option>
                                        {CREDIT_CYCLES.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </SelectWrap>
                            </div>
                            <div className="text-xs text-gray-500">Credited per cycle: <strong>{creditedPortion().toFixed(2)}</strong></div>
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                                <input type="checkbox" checked={carryFwd} onChange={(e) => setCarryFwd(e.target.checked)} />Carry Forward to Next Year
                            </label>
                            <Btn onClick={handleSubmit} loading={elPaidLeaveSaveLoading}><Save className="h-3.5 w-3.5" />Submit</Btn>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                            <div><Label required>Effective Date</Label><CustomDatePicker value={customDate} onChange={setCustomDate} /></div>
                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                                <input type="checkbox" checked={carryFwd} onChange={(e) => setCarryFwd(e.target.checked)} />Carry Forward to Next Year
                            </label>
                            {existingElPaidLeave?.leavetypeid === 7 ? (
                                <Btn variant="danger" onClick={handleDeleteCustom} loading={elPaidLeaveSaveLoading}><Trash2 className="h-3.5 w-3.5" />Remove Custom Rule</Btn>
                            ) : (
                                <Btn onClick={handleSubmit} loading={elPaidLeaveSaveLoading}><Save className="h-3.5 w-3.5" />Submit</Btn>
                            )}
                        </div>
                    )}
                </div>
            </SectionCard>

            <SectionCard>
                {elPaidLeavesLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : otherRows.length === 0 ? (
                    <EmptyState message="No EL/Paid leave rules configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Leave Type', 'Year', 'Group', 'No. of Leaves', 'Credited By', 'Carry Forward'].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {otherRows.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{leaveTypes.find((l) => l.LeaveTypeid === row.LeaveTypeId)?.LeaveTypeName || row.LeaveType}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EffectiveYear}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.GroupName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.NoofLeaves}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.Creditedby}</td>
                                        <td className="px-3 py-2.5"><Badge color={row.CarryforwardToNextYear === 'Yes' ? 'green' : 'gray'}>{row.CarryforwardToNextYear}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 7 — GRATUITY
// ═════════════════════════════════════════════════════════════════════════════

const GratuityTab = ({ currentUser, dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const { gratuityData, gratuityLoading, gratuitySaveLoading, gratuitySaveResult, gratuitySaveError } = useSelector(selectHRRules);
    const [dca, setDca] = useState('');
    const [subDca, setSubDca] = useState('');
    const [effDate, setEffDate] = useState('');
    const [years, setYears] = useState('');
    const [days, setDays] = useState('');

    useEffect(() => { dispatch(fetchGratuityRuleData()); }, [dispatch]);

    useEffect(() => {
        if (gratuityData) {
            setDca(gratuityData.DCA || ''); setSubDca(gratuityData.SubDCA || '');
            setEffDate(gratuityData.EffectiveDate || ''); setYears(gratuityData.ElgibilityInYear ?? '');
            setDays(gratuityData.InDays ?? '');
        }
    }, [gratuityData]);

    useEffect(() => {
        if (gratuitySaveResult) { toast.success('Gratuity rule saved.'); dispatch(clearGratuitySaveResult()); dispatch(fetchGratuityRuleData()); }
        if (gratuitySaveError) { toast.error(gratuitySaveError); dispatch(clearGratuitySaveResult()); }
    }, [gratuitySaveResult, gratuitySaveError]); // eslint-disable-line

    const handleDCAChange = (code) => { setDca(code); setSubDca(''); if (code && !subDcaByDca[code]) onLoadSubDca(code); };

    const handleSubmit = () => {
        if (!dca || !subDca || !effDate || !years || !days) { toast.warning('All fields are required.'); return; }
        if (parseInt(days, 10) > 31) { toast.warning('Days of Basic cannot exceed 31.'); return; }
        dispatch(submitGratuityRule({
            EffectiveDate: toDDMonYYYY(effDate), ElgibilityInYear: parseInt(years, 10), InDays: parseInt(days, 10),
            DCA: dca, SubDCA: subDca, CreatedBy: currentUser,
        }));
    };

    return (
        <SectionCard>
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">Gratuity Eligibility Rule</span></div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                <DCAPairSelector dcaValue={dca} subDcaValue={subDca} onDCAChange={handleDCAChange} onSubDCAChange={setSubDca}
                    dcaList={dcaList} dcaLoading={dcaLoading} subDcaList={subDcaByDca[dca] || []} subDcaLoading={subDcaLoadingByDca[dca]} />
                <div><Label required>Effective Date</Label><CustomDatePicker value={effDate} onChange={setEffDate} /></div>
                <div><Label required>Years of Tenure</Label><input className={inputCls} value={years} onChange={(e) => setYears(e.target.value)} inputMode="numeric" /></div>
                <div><Label required>Days of Basic (max 31)</Label><input className={inputCls} value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" /></div>
                <Btn onClick={handleSubmit} loading={gratuitySaveLoading || gratuityLoading}><Save className="h-3.5 w-3.5" />{gratuityData?.GratutityRuleId ? 'Update' : 'Submit'}</Btn>
            </div>
        </SectionCard>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 8 — ADVANCE
// ═════════════════════════════════════════════════════════════════════════════

const AdvanceTab = ({ currentUser, dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const { advanceGroups, advanceGroupsLoading, advanceRules, advanceRulesLoading, empCostCenters, empCostCentersLoading, advanceSaveLoading, advanceSaveResult, advanceSaveError } = useSelector(selectHRRules);
    const [groupId, setGroupId] = useState('');
    const [years, setYears] = useState('');
    const [effDate, setEffDate] = useState('');
    const [ltaExceed, setLtaExceed] = useState('');
    const [ltaNP, setLtaNP] = useState('');
    const [saExceed, setSaExceed] = useState('');
    const [saNP, setSaNP] = useState('');
    const [ltaDCA, setLtaDCA] = useState(''); const [ltaSubDCA, setLtaSubDCA] = useState('');
    const [saDCA, setSaDCA] = useState(''); const [saSubDCA, setSaSubDCA] = useState('');
    const [creditCC, setCreditCC] = useState('');

    const load = useCallback(() => {
        dispatch(fetchGroupsForAdvance()); dispatch(fetchAllAdvanceRuleData('Approved')); dispatch(fetchEmpCostCenters());
    }, [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (advanceSaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearAdvanceSaveResult());
            setGroupId(''); setYears(''); setEffDate(''); setLtaExceed(''); setLtaNP(''); setSaExceed(''); setSaNP('');
            setLtaDCA(''); setLtaSubDCA(''); setSaDCA(''); setSaSubDCA(''); setCreditCC('');
            load();
        }
        if (advanceSaveError) { toast.error(advanceSaveError); dispatch(clearAdvanceSaveResult()); }
    }, [advanceSaveResult, advanceSaveError]); // eslint-disable-line

    const handleDCA = (setter, subSetter) => (code) => { setter(code); subSetter(''); if (code && !subDcaByDca[code]) onLoadSubDca(code); };

    const handleSubmit = () => {
        if (!groupId || !years || !effDate || !ltaExceed || !ltaNP || !saExceed || !saNP || !ltaDCA || !ltaSubDCA || !saDCA || !saSubDCA || !creditCC) {
            toast.warning('All fields are required.'); return;
        }
        dispatch(submitAdvanceRule({
            AdvanceRuleData: {
                GroupId: parseInt(groupId, 10), EmpServiceYears: parseInt(years, 10), EffectiveDate: toDDMonYYYY(effDate),
                LTAAmountLimitExceed: ltaExceed, LTAApplicableForNP: ltaNP, SAAmountLimitExceed: saExceed, SAApplicableForNP: saNP,
                LTADCA: ltaDCA, LTASubDCA: ltaSubDCA, SADCA: saDCA, SASubDCA: saSubDCA, LTACreditCC: creditCC,
            },
            CreatedBy: currentUser,
        }));
    };

    const handleDelete = (row) => dispatch(submitDeleteAdvanceRule({ AdvanceRuleData: { AdvanceId: row.AdvanceId, GroupId: row.GroupId }, CreatedBy: currentUser }));

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">LTA & Salary Advance Rule</span></div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <Label required>Group</Label>
                        <SelectWrap loading={advanceGroupsLoading}>
                            <select className={selectCls} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                                <option value="">— Select —</option>
                                {advanceGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    <div><Label required>Service Years (1-8)</Label><input className={inputCls} value={years} onChange={(e) => setYears(e.target.value)} inputMode="numeric" /></div>
                    <div><Label required>Effective Date</Label><CustomDatePicker value={effDate} onChange={setEffDate} /></div>
                    <div>
                        <Label required>Credit To Cost Center</Label>
                        <SelectWrap loading={empCostCentersLoading}>
                            <select className={selectCls} value={creditCC} onChange={(e) => setCreditCC(e.target.value)}>
                                <option value="">— Select —</option>
                                {empCostCenters.map((c) => <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Name}</option>)}
                            </select>
                        </SelectWrap>
                    </div>

                    <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-900/30">
                        <div>
                            <Label required>LTA Amount Limit Exceed</Label>
                            <SelectWrap><select className={selectCls} value={ltaExceed} onChange={(e) => setLtaExceed(e.target.value)}><option value="">—</option>{YES_NO.map((v) => <option key={v}>{v}</option>)}</select></SelectWrap>
                        </div>
                        <div>
                            <Label required>LTA Applicable For NP</Label>
                            <SelectWrap><select className={selectCls} value={ltaNP} onChange={(e) => setLtaNP(e.target.value)}><option value="">—</option>{YES_NO.map((v) => <option key={v}>{v}</option>)}</select></SelectWrap>
                        </div>
                        <DCAPairSelector label="LTA DCA" dcaValue={ltaDCA} subDcaValue={ltaSubDCA} onDCAChange={handleDCA(setLtaDCA, setLtaSubDCA)} onSubDCAChange={setLtaSubDCA}
                            dcaList={dcaList} dcaLoading={dcaLoading} subDcaList={subDcaByDca[ltaDCA] || []} subDcaLoading={subDcaLoadingByDca[ltaDCA]} />
                    </div>

                    <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-900/30">
                        <div>
                            <Label required>SA Amount Limit Exceed</Label>
                            <SelectWrap><select className={selectCls} value={saExceed} onChange={(e) => setSaExceed(e.target.value)}><option value="">—</option>{YES_NO.map((v) => <option key={v}>{v}</option>)}</select></SelectWrap>
                        </div>
                        <div>
                            <Label required>SA Applicable For NP</Label>
                            <SelectWrap><select className={selectCls} value={saNP} onChange={(e) => setSaNP(e.target.value)}><option value="">—</option>{YES_NO.map((v) => <option key={v}>{v}</option>)}</select></SelectWrap>
                        </div>
                        <DCAPairSelector label="SA DCA" dcaValue={saDCA} subDcaValue={saSubDCA} onDCAChange={handleDCA(setSaDCA, setSaSubDCA)} onSubDCAChange={setSaSubDCA}
                            dcaList={dcaList} dcaLoading={dcaLoading} subDcaList={subDcaByDca[saDCA] || []} subDcaLoading={subDcaLoadingByDca[saDCA]} />
                    </div>

                    <Btn onClick={handleSubmit} loading={advanceSaveLoading}><Save className="h-3.5 w-3.5" />Submit</Btn>
                </div>
            </SectionCard>

            <SectionCard>
                {advanceRulesLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : advanceRules.length === 0 ? (
                    <EmptyState message="No advance rules configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Eff. Date', 'Years', 'LTA Exceed', 'LTA NP', 'SA Exceed', 'SA NP', 'Group', 'LTA Head', 'SA Head', 'Credit CC', ''].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {advanceRules.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EffectiveDate}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EmpServiceYears}</td>
                                        <td className="px-3 py-2.5">{row.LTAAmountLimitExceed}</td>
                                        <td className="px-3 py-2.5">{row.LTAApplicableForNP}</td>
                                        <td className="px-3 py-2.5">{row.SAAmountLimitExceed}</td>
                                        <td className="px-3 py-2.5">{row.SAApplicableForNP}</td>
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.GroupId === 0 ? 'All' : row.GroupName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.LTADCAName}/{row.LTASubDCAName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.SADCAName}/{row.SASubDCAName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.LTACreditCCName}</td>
                                        <td className="px-3 py-2.5"><button onClick={() => handleDelete(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 9 — NOTICE PERIOD
// ═════════════════════════════════════════════════════════════════════════════

const NoticePeriodTab = ({ currentUser }) => {
    const dispatch = useDispatch();
    const { noticePeriodGroups, noticePeriodGroupsLoading, notificationRoles, notificationRolesLoading, noticePeriodData, noticePeriodDataLoading, noticePeriodSaveLoading, noticePeriodSaveResult, noticePeriodSaveError } = useSelector(selectHRRules);
    const [groupId, setGroupId] = useState('');
    const [inProb, setInProb] = useState('');
    const [afterProb, setAfterProb] = useState('');
    const [effDate, setEffDate] = useState('');
    const [probAge, setProbAge] = useState('');
    const [roleId, setRoleId] = useState('');

    const load = useCallback(() => {
        dispatch(fetchGroupsForNoticePeriod()); dispatch(fetchNotificationToRole()); dispatch(fetchNoticePeriodData());
    }, [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (noticePeriodSaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearNoticePeriodSaveResult());
            setGroupId(''); setInProb(''); setAfterProb(''); setEffDate(''); setProbAge(''); setRoleId('');
            load();
        }
        if (noticePeriodSaveError) { toast.error(noticePeriodSaveError); dispatch(clearNoticePeriodSaveResult()); }
    }, [noticePeriodSaveResult, noticePeriodSaveError]); // eslint-disable-line

    const handleSubmit = () => {
        if (!groupId || !inProb || !afterProb || !effDate || !probAge || !roleId) { toast.warning('All fields are required.'); return; }
        dispatch(submitNoticePeriodRule({
            EmployeeNoticePeriodRulesData: {
                GroupId: parseInt(groupId, 10), InProbServingNoticePeriod: parseInt(inProb, 10), AfterProbServingNoticePeriod: parseInt(afterProb, 10),
                NPEffectiveDate: toDDMonYYYY(effDate), ProbationAge: parseInt(probAge, 10), NotificationRoleId: parseInt(roleId, 10),
            },
            CreatedBy: currentUser,
        }));
    };

    const handleDelete = (row) => dispatch(submitDeleteNoticePeriod({ EmployeeNoticePeriodRulesData: { NoticePeriodId: row.NoticePeriodId, GroupId: row.GroupId }, CreatedBy: currentUser }));

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">Notice Period Rule</span></div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <Label required>Group</Label>
                        <SelectWrap loading={noticePeriodGroupsLoading}>
                            <select className={selectCls} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                                <option value="">— Select —</option>
                                {noticePeriodGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    <div><Label required>Notice Days (In Probation)</Label><input className={inputCls} value={inProb} onChange={(e) => setInProb(e.target.value)} inputMode="numeric" /></div>
                    <div><Label required>Notice Days (After Probation)</Label><input className={inputCls} value={afterProb} onChange={(e) => setAfterProb(e.target.value)} inputMode="numeric" /></div>
                    <div><Label required>Effective Date</Label><CustomDatePicker value={effDate} onChange={setEffDate} /></div>
                    <div><Label required>Probation Age (months)</Label><input className={inputCls} value={probAge} onChange={(e) => setProbAge(e.target.value)} inputMode="numeric" /></div>
                    <div>
                        <Label required>Notify Role</Label>
                        <SelectWrap loading={notificationRolesLoading}>
                            <select className={selectCls} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                                <option value="">— Select —</option>
                                {notificationRoles.map((r) => <option key={r.Roleid} value={r.Roleid}>{r.RoleName}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    <Btn onClick={handleSubmit} loading={noticePeriodSaveLoading}><Save className="h-3.5 w-3.5" />Submit</Btn>
                </div>
            </SectionCard>

            <SectionCard>
                {noticePeriodDataLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : noticePeriodData.length === 0 ? (
                    <EmptyState message="No notice period rules configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['In Probation', 'After Probation', 'Eff. Date', 'Group', 'Probation Age', 'Notify Role', ''].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {noticePeriodData.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.InProbServingNoticePeriod}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.AfterProbServingNoticePeriod}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.NPEffectiveDate}</td>
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.GroupName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.ProbationAge}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.NotificationRole}</td>
                                        <td className="px-3 py-2.5"><button onClick={() => handleDelete(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 10 — TAX (Professional Tax)
// ═════════════════════════════════════════════════════════════════════════════

const TaxTab = ({ currentUser, dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const { taxGroups, taxGroupsLoading, taxStates, taxStatesLoading, taxData, taxDataLoading, taxSaveLoading, taxSaveResult, taxSaveError } = useSelector(selectHRRules);
    const [groupId, setGroupId] = useState('');
    const [effDate, setEffDate] = useState('');
    const [applicableTo, setApplicableTo] = useState('');
    const [pct, setPct] = useState('');
    const [dca, setDca] = useState(''); const [subDca, setSubDca] = useState('');
    const [selStates, setSelStates] = useState([]);

    const load = useCallback(() => { dispatch(fetchGroupsForTaxRule()); dispatch(fetchProfessionalTaxData()); }, [dispatch]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (taxSaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearTaxSaveResult());
            setGroupId(''); setEffDate(''); setApplicableTo(''); setPct(''); setDca(''); setSubDca(''); setSelStates([]);
            load();
        }
        if (taxSaveError) { toast.error(taxSaveError); dispatch(clearTaxSaveResult()); }
    }, [taxSaveResult, taxSaveError]); // eslint-disable-line

    const handleGroupChange = (val) => { setGroupId(val); setSelStates([]); if (val) dispatch(fetchStatesListForTax(val)); };
    const handleDCAChange = (code) => { setDca(code); setSubDca(''); if (code && !subDcaByDca[code]) onLoadSubDca(code); };
    const toggleState = (id) => setSelStates((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const handleSubmit = () => {
        if (!groupId || !effDate || !applicableTo || !pct || !dca || !subDca || selStates.length === 0) { toast.warning('All fields are required.'); return; }
        dispatch(submitTaxRule({
            GroupId: parseInt(groupId, 10), EffectiveDate: toDDMonYYYY(effDate), ApplicaleToWhome: applicableTo,
            PercentageInSalary: parseFloat(pct), State: joinIds(selStates), DCA: dca, SubDCA: subDca, CreatedBy: currentUser,
        }));
    };

    const handleDelete = (row) => dispatch(submitDeleteTaxRule({ GroupId: row.GroupId, StateId: row.StateId, CreatedBy: currentUser }));

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">Professional Tax Rule</span></div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <Label required>Group</Label>
                        <SelectWrap loading={taxGroupsLoading}>
                            <select className={selectCls} value={groupId} onChange={(e) => handleGroupChange(e.target.value)}>
                                <option value="">— Select —</option>
                                {taxGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    <div><Label required>Effective Date</Label><CustomDatePicker value={effDate} onChange={setEffDate} maxDate={new Date()} /></div>
                    <div><Label required>Applicable To (Salary ≥)</Label><input className={inputCls} value={applicableTo} onChange={(e) => setApplicableTo(e.target.value)} inputMode="decimal" /></div>
                    <div><Label required>Percentage in Salary</Label><input className={inputCls} value={pct} onChange={(e) => setPct(e.target.value)} inputMode="decimal" /></div>
                    <DCAPairSelector dcaValue={dca} subDcaValue={subDca} onDCAChange={handleDCAChange} onSubDCAChange={setSubDca}
                        dcaList={dcaList} dcaLoading={dcaLoading} subDcaList={subDcaByDca[dca] || []} subDcaLoading={subDcaLoadingByDca[dca]} />
                    <div className="md:col-span-2">
                        <Label required>States</Label>
                        <MultiCheckList options={taxStates} selected={selStates} onToggle={toggleState} loading={taxStatesLoading} />
                    </div>
                    <Btn onClick={handleSubmit} loading={taxSaveLoading}><Save className="h-3.5 w-3.5" />Submit</Btn>
                </div>
            </SectionCard>

            <SectionCard>
                {taxDataLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : taxData.length === 0 ? (
                    <EmptyState message="No tax rules configured yet." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Eff. Date', 'Group', 'Applicable To', '%', 'State', 'DCA', 'SubDCA', ''].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {taxData.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EffectiveDate}</td>
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.GroupName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.ApplicaleToWhome}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.PercentageInSalary}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.State}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.DCAName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.SubDCAName}</td>
                                        <td className="px-3 py-2.5"><button onClick={() => handleDelete(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// TAB 11 — OFFICIAL TRAVEL
// ═════════════════════════════════════════════════════════════════════════════

const TravelTab = ({ currentUser, dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca }) => {
    const dispatch = useDispatch();
    const { travelGroups, travelGroupsLoading, travelData, travelGridType, travelDataLoading, travelSaveLoading, travelSaveResult, travelSaveError } = useSelector(selectHRRules);
    const [groupId, setGroupId] = useState('');
    const [effDate, setEffDate] = useState('');
    const [maxAmount, setMaxAmount] = useState('');
    const [claimDays, setClaimDays] = useState('');
    const [dca, setDca] = useState(''); const [subDca, setSubDca] = useState('');
    const [viewType, setViewType] = useState('CurrentlyActive');

    const load = useCallback((type) => { dispatch(fetchGroupsForTravelRule()); dispatch(fetchAllHRTravelExpRules(type)); }, [dispatch]);
    useEffect(() => { load(viewType); }, [load, viewType]);

    useEffect(() => {
        if (travelSaveResult) {
            toast.success('Saved successfully.');
            dispatch(clearTravelSaveResult());
            setGroupId(''); setEffDate(''); setMaxAmount(''); setClaimDays(''); setDca(''); setSubDca('');
            load(viewType);
        }
        if (travelSaveError) { toast.error(travelSaveError); dispatch(clearTravelSaveResult()); }
    }, [travelSaveResult, travelSaveError]); // eslint-disable-line

    const handleDCAChange = (code) => { setDca(code); setSubDca(''); if (code && !subDcaByDca[code]) onLoadSubDca(code); };

    const handleSubmit = () => {
        if (!groupId || !effDate || !maxAmount || !claimDays || !dca || !subDca) { toast.warning('All fields are required.'); return; }
        dispatch(submitTravelRule({
            GroupId: parseInt(groupId, 10), EffectiveDate: toDDMonYYYY(effDate), MaxAmount: parseFloat(maxAmount),
            ClaimWithinDays: parseInt(claimDays, 10), DCACode: dca, SubDCACode: subDca, CreatedBy: currentUser,
        }));
    };

    const handleDelete = (row) => dispatch(submitDeleteTravelRule({ Id: row.Id, GroupId: row.GroupId, CreatedBy: currentUser }));

    return (
        <div className="space-y-5">
            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700"><span className="text-sm font-bold text-gray-800 dark:text-gray-100">Official Travel Expense Rule</span></div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <Label required>Group</Label>
                        <SelectWrap loading={travelGroupsLoading}>
                            <select className={selectCls} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                                <option value="">— Select —</option>
                                {travelGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                    <div><Label required>Effective Date</Label><CustomDatePicker value={effDate} onChange={setEffDate} maxDate={new Date()} /></div>
                    <div><Label required>Max Amount</Label><input className={inputCls} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} inputMode="decimal" /></div>
                    <div><Label required>Claim Within Days</Label><input className={inputCls} value={claimDays} onChange={(e) => setClaimDays(e.target.value)} inputMode="numeric" /></div>
                    <DCAPairSelector dcaValue={dca} subDcaValue={subDca} onDCAChange={handleDCAChange} onSubDCAChange={setSubDca}
                        dcaList={dcaList} dcaLoading={dcaLoading} subDcaList={subDcaByDca[dca] || []} subDcaLoading={subDcaLoadingByDca[dca]} />
                    <Btn onClick={handleSubmit} loading={travelSaveLoading}><Save className="h-3.5 w-3.5" />Submit</Btn>
                </div>
            </SectionCard>

            <SectionCard>
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex gap-2">
                    <button onClick={() => setViewType('CurrentlyActive')} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${viewType === 'CurrentlyActive' ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>Currently Active</button>
                    <button onClick={() => setViewType('History')} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${viewType === 'History' ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>History</button>
                </div>
                {travelDataLoading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : travelData.length === 0 ? (
                    <EmptyState message="No travel expense rules found." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Eff. Date', 'Group', 'Max Amount', 'Claim Days', 'Status', 'DCA', 'SubDCA', travelGridType === 'CurrentlyActive' ? '' : null].filter((h) => h !== null).map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {travelData.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.EffectiveDate}</td>
                                        <td className="px-3 py-2.5 font-semibold text-gray-800 dark:text-gray-100">{row.GroupName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.MaxAmount}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.ClaimWithinDays}</td>
                                        <td className="px-3 py-2.5"><Badge color={row.Status === 'Active' ? 'green' : 'gray'}>{row.Status}</Badge></td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.DCAName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.SubDCAName}</td>
                                        {travelGridType === 'CurrentlyActive' && (
                                            <td className="px-3 py-2.5"><button onClick={() => handleDelete(row)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button></td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const HRRules = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const { dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca } = useSelector(selectHRRules);

    const [activeTab, setActiveTab] = useState('groups');

    useEffect(() => { dispatch(fetchDCAForHRRules()); }, [dispatch]);

    const onLoadSubDca = useCallback((code) => dispatch(fetchSubDCAByDCACode(code)), [dispatch]);

    const dcaProps = { dcaList, dcaLoading, subDcaByDca, subDcaLoadingByDca, onLoadSubDca };

    const tabContent = {
        groups:       <GroupsTab currentUser={currentUser} />,
        category:     <CategoryTab currentUser={currentUser} />,
        salarywages:  <SalaryMinWagesTab currentUser={currentUser} {...dcaProps} />,
        pfesi:        <PFESITab currentUser={currentUser} {...dcaProps} />,
        holidays:     <HolidaysTab currentUser={currentUser} />,
        elpaidleave:  <ELPaidLeaveTab currentUser={currentUser} />,
        gratuity:     <GratuityTab currentUser={currentUser} {...dcaProps} />,
        advance:      <AdvanceTab currentUser={currentUser} {...dcaProps} />,
        noticeperiod: <NoticePeriodTab currentUser={currentUser} />,
        tax:          <TaxTab currentUser={currentUser} {...dcaProps} />,
        travel:       <TravelTab currentUser={currentUser} {...dcaProps} />,
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Settings className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">HR Rules</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Groups, categories, salary/wages, statutory and leave configuration rules</p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-0 overflow-x-auto">
                        {TABS.map(({ id, label, icon: Icon }) => {
                            const active = activeTab === id;
                            return (
                                <button key={id} onClick={() => setActiveTab(id)}
                                    className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${active ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'}`}>
                                    <Icon className="h-4 w-4" />{label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6">
                {tabContent[activeTab]}
            </div>
        </div>
    );
};

export default HRRules;
